import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Card, CardContent, Button, Chip, CircularProgress,
    Alert, TextField, Divider, IconButton, Paper, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
    Add, Remove, ArrowBack, CheckCircle,
    Person, Phone, LocationOn, Home, Edit as EditIcon, MyLocation,
    LocalShipping, Store as StorePickupIcon,
    ShoppingCart as CartIcon, ExpandLess, WhatsApp as WhatsAppIcon,
    CalendarMonth as SubscriptionIcon,
} from '@mui/icons-material';
import { publicStoreAPI, authAPI } from '../api';
import { normalizeMobile, getMobileError } from '../utils/mobile';
import AuthPage from './AuthPage';
import { useCategoryScrollspy, StickyCategoryBar, sectionScrollMargin } from './ProductCategoryNav';
import CartReviewSheet from './CartReviewSheet';
import ProductDetailSheet from './ProductDetailSheet';
import BusinessLogo from './common/BusinessLogo';
import ProductImage from './common/ProductImage';
import { groupByCategory } from '../utils/groupByCategory';

// WhatsApp number — digits only, with India country code for 10-digit numbers.
const waNumber = (phone) => {
    const d = String(phone || '').replace(/\D/g, '');
    return d.length === 10 ? '91' + d : d;
};

// Safely turn an axios/FastAPI error into a display string. `detail` may be a
// plain string, or a 422 validation array of { msg, loc, ... } objects — the
// latter must never be rendered directly as a React child.
const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(x => x?.msg).filter(Boolean).join(', ') || fallback;
    if (d && typeof d === 'object' && d.msg) return d.msg;
    return fallback;
};

function haversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmt(amount) {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

// Local-date YYYY-MM-DD for tomorrow. Built from the local clock (not toISOString, which
// shifts to UTC and can read a day early before 05:30 IST).
const planTomorrowISO = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Default note shown on the store when the vendor hasn't set their own Store Note.
const DEFAULT_ORDER_NOTE = 'Prices are indicative and may vary. Your order may be revised by the vendor before confirmation.';

function SectionLabel({ children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
            <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {children}
            </Typography>
        </Box>
    );
}

export default function PublicStorePage({ businessId, onBack }) {
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // undefined = still checking, null = not logged in, object = user
    const [loggedInUser, setLoggedInUser] = useState(undefined);
    // Customer profile for this specific business (null if not a registered customer)
    const [customerProfile, setCustomerProfile] = useState(undefined);

    const [cart, setCart] = useState({});
    const [detailProduct, setDetailProduct] = useState(null);
    const [cartReviewOpen, setCartReviewOpen] = useState(false);
    const [showAuth, setShowAuth] = useState(false);
    // Plans (subscriptions) — same browse -> cart -> checkout shape as orders.
    const [planProducts, setPlanProducts] = useState([]);
    const [planCart, setPlanCart] = useState({});      // { productId: qty }
    const [planStartDate, setPlanStartDate] = useState(planTomorrowISO());
    const [planNote, setPlanNote] = useState('');
    const [planError, setPlanError] = useState('');
    const [planSubmitting, setPlanSubmitting] = useState(false);
    const [storeMode, setStoreMode] = useState('order'); // 'order' | 'subscription'
    const [orderNotes, setOrderNotes] = useState('');
    const [fulfillmentType, setFulfillmentType] = useState(null); // 'delivery' | 'pickup' | null (unchosen)
    const [step, setStep] = useState('browse'); // 'browse' | 'checkout' | 'confirmed'

    const [form, setForm] = useState({ name: '', phone: '', address: '' });
    const [formAreaId, setFormAreaId] = useState(null);
    const [gpsAreaDetected, setGpsAreaDetected] = useState(false);
    const [editingAddress, setEditingAddress] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [confirmedOrder, setConfirmedOrder] = useState(null);

    // Inline registration dialog
    const [regOpen, setRegOpen] = useState(false);
    const [regStep, setRegStep] = useState('send-otp'); // 'send-otp' | 'verify-otp'
    const [regMobile, setRegMobile] = useState('');
    const [regName, setRegName] = useState('');
    const [regOtp, setRegOtp] = useState('');
    const [regPin, setRegPin] = useState('');
    const [regLoading, setRegLoading] = useState(false);
    const [regError, setRegError] = useState('');

    // Post-order mobile check (does the phone already have an account?)
    const [mobileCheck, setMobileCheck] = useState(null); // null | {exists, has_password}

    // Inline login dialog (for existing users)
    const [loginOpen, setLoginOpen] = useState(false);
    const [loginPin, setLoginPin] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    useEffect(() => {
        publicStoreAPI.getCatalog(businessId)
            .then(res => setCatalog(res.data))
            .catch(() => setError('Could not load store. Please try again.'))
            .finally(() => setLoading(false));

        publicStoreAPI.getPlanProducts(businessId)
            .then(res => setPlanProducts(res.data || []))
            .catch(() => setPlanProducts([]));

        const token = localStorage.getItem('access_token');
        if (token) {
            publicStoreAPI.getAuthUser(token)
                .then(res => {
                    setLoggedInUser(res.data);
                    setForm(f => ({ ...f, name: res.data.f_name || '', phone: res.data.f_mobile || '' }));
                    // Try to fetch their customer profile for this business
                    return publicStoreAPI.getMyProfile(businessId, token);
                })
                .then(res => {
                    const profile = res.data;
                    setCustomerProfile(profile);
                    // Pre-fill form with stored details
                    setForm({ name: profile.f_name || '', phone: profile.f_mobile || '', address: profile.f_address_line1 || '' });
                    setFormAreaId(profile.f_area_id || null);
                })
                .catch(() => {
                    // Either token expired or not a customer of this business — both are fine
                    setCustomerProfile(null);
                });
        } else {
            setLoggedInUser(null);
            setCustomerProfile(null);
        }
    }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Default the fulfillment choice to what the business actually offers.
    useEffect(() => {
        const mode = catalog?.business?.f_fulfillment_mode;
        if (mode === 'pickup') setFulfillmentType('pickup');
        else if (mode === 'delivery') setFulfillmentType('delivery');
        // 'both' → leave null so the customer must actively choose
    }, [catalog]);

    // Auto-select area by GPS when entering checkout
    useEffect(() => {
        if (step !== 'checkout') return;
        if (formAreaId) return; // already selected — don't override
        const geoAreas = (catalog?.areas || []).filter(a => a.lat != null && a.lng != null);
        if (geoAreas.length === 0) return;
        if (!navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                let best = null, bestDist = Infinity;
                for (const a of geoAreas) {
                    const d = haversineKm(latitude, longitude, a.lat, a.lng);
                    if (d < bestDist) { bestDist = d; best = a; }
                }
                if (best && bestDist <= 1.0) {
                    setFormAreaId(best.f_id);
                    setGpsAreaDetected(true);
                }
            },
            () => {}, // silently ignore — user can still pick manually
            { timeout: 5000, enableHighAccuracy: false }
        );
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Cart ────────────────────────────────────────────────────────────────
    const addProduct = (product) => {
        const min  = parseFloat(product.f_min_order_qty) || 1;
        const step = parseFloat(product.f_increment_order_qty) || 1;
        setCart(prev => ({ ...prev, [product.f_id]: prev[product.f_id] ? prev[product.f_id] + step : min }));
    };
    const removeProduct = (product) => {
        const min  = parseFloat(product.f_min_order_qty) || 1;
        const step = parseFloat(product.f_increment_order_qty) || 1;
        setCart(prev => {
            const next = (prev[product.f_id] || 0) - step;
            if (next < min) { const n = { ...prev }; delete n[product.f_id]; return n; }
            return { ...prev, [product.f_id]: next };
        });
    };

    const cartProducts = (catalog?.products || []).filter(p => cart[p.f_id] > 0);
    const cartSubtotal = cartProducts.reduce((s, p) => {
        const net = (parseFloat(p.current_price) || 0) - (parseFloat(p.current_discount) || 0);
        return s + net * (cart[p.f_id] || 0);
    }, 0);
    const cartCount = Object.values(cart).reduce((s, q) => s + q, 0);

    const areas      = catalog?.areas || [];
    const biz        = catalog?.business || {};
    const fulfillmentMode = biz.f_fulfillment_mode || 'delivery';
    const showFulfillmentToggle = fulfillmentMode === 'both';
    const isKnownCustomer = !!customerProfile;

    // Group products by category. A product can be in several categories (f_categories);
    // it shows under each. Category order follows the vendor's saved order.
    const { grouped, categories } = useMemo(
        () => groupByCategory(catalog?.products || [], 'f_categories', 'f_category', catalog?.category_order || []),
        [catalog]
    );
    const { selectedCategory, scrollToCategory, stickyBarRef, setSectionRef, setChipRef } =
        useCategoryScrollspy(categories);
    // Plans get the same category grouping + scrollspy so the plan browser looks like the store.
    const { grouped: planGrouped, categories: planCategories } = useMemo(
        () => groupByCategory(planProducts, 'f_categories', 'f_category', catalog?.category_order || []),
        [planProducts, catalog]
    );
    const planSpy = useCategoryScrollspy(planCategories);
    // No fixed app bar above the store, so the sticky bar must cover the status-bar /
    // notch strip itself: pin at 0 and pad its content below the safe-area inset, so its
    // (opaque) background fills the notch and products don't show through above it.
    const SAFE_TOP = 'env(safe-area-inset-top, 0px)';

    // Delivery charge: area-specific → business default → 0
    const selectedArea = areas.find(a => a.f_id === formAreaId);
    const deliveryCharge = fulfillmentType === 'pickup' ? 0
        : (selectedArea?.f_delivery_charge != null ? selectedArea.f_delivery_charge
        : (biz.f_default_delivery_charge != null ? biz.f_default_delivery_charge : 0));
    const cartTotal = cartSubtotal + deliveryCharge;

    const selectedAreaName = selectedArea?.f_name || customerProfile?.area_name || null;

    const phoneError = form.phone.trim() ? getMobileError(form.phone) : '';

    // canSubmit
    const canSubmit = (() => {
        const isPickup = fulfillmentType === 'pickup';
        // When both modes are offered, the customer must pick one first.
        if (showFulfillmentToggle && !fulfillmentType) return false;
        const areaOk = isPickup || areas.length === 0 || formAreaId != null;
        if (isKnownCustomer && !editingAddress) return areaOk;
        if (loggedInUser) return areaOk;
        return form.name.trim().length > 0 && form.phone.trim().length > 0 && !phoneError && areaOk;
    })();

    // ── Place order ─────────────────────────────────────────────────────────
    const handlePlaceOrder = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setSubmitError('');
        const name  = form.name  || loggedInUser?.f_name  || '';
        const phone = normalizeMobile(form.phone || loggedInUser?.f_mobile || '');
        try {
            const res = await publicStoreAPI.placeOrder(businessId, {
                customer_name:    name.trim(),
                customer_phone:   phone.trim(),
                area_id:          fulfillmentType === 'pickup' ? null : (formAreaId || null),
                delivery_address: form.address.trim() || null,  // optional, kept for pickup too
                fulfillment_type: fulfillmentType,
                notes:            orderNotes.trim() || null,
                items: cartProducts.map(p => ({ product_id: p.f_id, quantity: cart[p.f_id] })),
            });
            setConfirmedOrder(res.data);
            setStep('confirmed');
            // Silently check if the guest's phone already has an account
            if (!loggedInUser && phone) {
                authAPI.checkMobile(phone)
                    .then(r => setMobileCheck(r.data))
                    .catch(() => {}); // ignore errors — UI degrades gracefully
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setSubmitError(typeof detail === 'string' ? detail : 'Failed to place order. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Plans ────────────────────────────────────────────────────────────────
    const planItems = Object.entries(planCart)
        .filter(([, q]) => q > 0)
        .map(([pid, q]) => ({ product_id: Number(pid), quantity: q }));
    const planCount = planItems.length;

    const setPlanQty = (productId, delta) => setPlanCart(prev => {
        const next = Math.max(0, (prev[productId] || 0) + delta);
        const copy = { ...prev };
        if (next === 0) delete copy[productId]; else copy[productId] = next;
        return copy;
    });

    // The whole cart is one all-or-nothing request the vendor approves, sharing one start date.
    const submitPlanSignup = async () => {
        setPlanError('');
        if (planCount === 0) { setPlanError('Please choose at least one plan.'); return; }
        if (!form.name?.trim()) { setPlanError('Please enter your name.'); return; }
        const perr = getMobileError(form.phone);
        if (perr) { setPlanError(perr); return; }
        if (!planStartDate || planStartDate < planTomorrowISO()) {
            setPlanError('Please choose tomorrow or a later start date.'); return;
        }
        setPlanSubmitting(true);
        try {
            await publicStoreAPI.planSignup(businessId, {
                customer_name: form.name.trim(),
                customer_phone: normalizeMobile(form.phone),
                area_id: formAreaId || null,
                delivery_address: form.address?.trim() || null,
                items: planItems,
                effective_from_date: planStartDate,
                note: planNote.trim() || null,
            });
            setStep('plan-confirmed');
        } catch (e) {
            setPlanError(errMsg(e, 'Could not send your request. Please try again.'));
        } finally {
            setPlanSubmitting(false);
        }
    };

    const openRegDialog = () => {
        setRegMobile(form.phone || '');
        setRegName(form.name || '');
        setRegOtp('');
        setRegPin('');
        setRegError('');
        setRegStep('send-otp');
        setRegOpen(true);
    };

    const openLoginDialog = () => {
        setLoginPin('');
        setLoginError('');
        setLoginOpen(true);
    };

    const handleLogin = async () => {
        if (loginPin.length !== 6) { setLoginError('Enter your 6-digit PIN'); return; }
        setLoginLoading(true);
        setLoginError('');
        try {
            const mobile = normalizeMobile(form.phone);
            const res = await authAPI.loginWithPassword(mobile, loginPin, navigator.userAgent);
            localStorage.setItem('access_token', res.data.access_token);
            localStorage.setItem('vendora.customerTab', '0'); // land on Home → Orders
            setLoginOpen(false);
            window.location.href = '/?view=customer';
        } catch (e) {
            setLoginError(errMsg(e, 'Wrong PIN. Please try again.'));
        } finally {
            setLoginLoading(false);
        }
    };

    const handleSendOtp = async () => {
        const mobileErr = getMobileError(regMobile);
        if (!regMobile.trim()) { setRegError('Enter your mobile number'); return; }
        if (mobileErr) { setRegError(mobileErr); return; }
        const normalizedMobile = normalizeMobile(regMobile);
        setRegLoading(true);
        setRegError('');
        try {
            await authAPI.sendOTP(normalizedMobile, 'sms');
            setRegMobile(normalizedMobile);
            setRegStep('verify-otp');
        } catch (e) {
            setRegError(errMsg(e, 'Failed to send OTP. Please try again.'));
        } finally {
            setRegLoading(false);
        }
    };

    const handleVerifyAndRegister = async () => {
        if (regOtp.length !== 6) { setRegError('Enter the 6-digit OTP'); return; }
        if (regPin.length !== 6) { setRegError('PIN must be exactly 6 digits'); return; }
        setRegLoading(true);
        setRegError('');
        try {
            const res = await authAPI.verifyOTPAndSetPassword(regMobile, regOtp, regPin, regName.trim() || undefined, businessId);
            localStorage.setItem('access_token', res.data.access_token);
            localStorage.setItem('vendora.customerTab', '0'); // land on Home → Orders
            setRegOpen(false);
            window.location.href = '/?view=customer';
        } catch (e) {
            setRegError(errMsg(e, 'Invalid OTP or something went wrong.'));
        } finally {
            setRegLoading(false);
        }
    };

    const handlePublicLogout = () => {
        localStorage.removeItem('access_token');
        setLoggedInUser(null);
        setCustomerProfile(null);
        setForm({ name: '', phone: '', address: '' });
        setFormAreaId(null);
        setGpsAreaDetected(false);
        setEditingAddress(false);
    };

    const resetStore = () => {
        setCart({});
        setFulfillmentType('delivery');
        // Restore pre-filled form from profile or logged-in user
        if (customerProfile) {
            setForm({ name: customerProfile.f_name || '', phone: customerProfile.f_mobile || '', address: customerProfile.f_address_line1 || '' });
            setFormAreaId(customerProfile.f_area_id || null);
        } else {
            setForm({ name: loggedInUser?.f_name || '', phone: loggedInUser?.f_mobile || '', address: '' });
            setFormAreaId(null);
        }
        setGpsAreaDetected(false);
        setEditingAddress(false);
        setConfirmedOrder(null);
        setOrderNotes('');
        setStep('browse');
    };

    // ── Auth overlay (login / create account) ─────────────────────────────────
    // On success, land the customer back on THIS business inside their dashboard.
    if (showAuth) {
        return (
            <AuthPage
                signupBusinessId={businessId}
                onLoginSuccess={() => { window.location.href = `/?view=customer&business_id=${businessId}`; }}
                onBack={() => setShowAuth(false)}
            />
        );
    }

    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading || loggedInUser === undefined || customerProfile === undefined) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', pt: 'env(safe-area-inset-top, 0px)' }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error || !catalog) {
        return <Box sx={{ p: 3, textAlign: 'center', mt: 8 }}><Alert severity="error">{error || 'Store not found'}</Alert></Box>;
    }

    // ── Confirmed ───────────────────────────────────────────────────────────
    if (step === 'confirmed' && confirmedOrder) {
        return (
            <>
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, pb: 2, pt: 'calc(16px + env(safe-area-inset-top, 0px))' }}>
                <Paper elevation={0} sx={{ maxWidth: 440, width: '100%', borderRadius: 3, p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                    <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 1 }} />
                    <Typography variant="h5" fontWeight={800} gutterBottom>Order Placed!</Typography>
                    <Typography variant="body1" color="text.secondary">
                        Order <strong>#{confirmedOrder.order_id}</strong> · <strong>{fmt(confirmedOrder.total_amount)}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {biz.f_name} will confirm your order shortly.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
                        The vendor will send you an invoice — you can make the payment directly to them at that time.
                    </Typography>
                    {biz.f_phone && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <Button fullWidth variant="outlined" component="a" href={`tel:${biz.f_phone}`}
                                startIcon={<Phone />} sx={{ height: 44, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                                Call
                            </Button>
                            <Button fullWidth variant="contained" component="a" href={`https://wa.me/${waNumber(biz.f_phone)}`} target="_blank" rel="noopener"
                                startIcon={<WhatsAppIcon />} sx={{ height: 44, textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' } }}>
                                WhatsApp
                            </Button>
                        </Box>
                    )}
                    <Button fullWidth variant="contained" size="large" onClick={resetStore}
                        sx={{ height: 48, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                        Place Another Order
                    </Button>

                    {loggedInUser && (
                        <Button
                            fullWidth variant="outlined" size="large"
                            onClick={() => { localStorage.setItem('vendora.customerTab', '0'); window.location.href = '/?view=customer'; }}
                            sx={{ height: 48, textTransform: 'none', fontWeight: 700, borderRadius: 2, mt: 1.5 }}
                        >
                            Go to My Dashboard
                        </Button>
                    )}

                    {!isKnownCustomer && (
                        <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            {!loggedInUser ? (
                                mobileCheck?.exists ? (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            You already have an account. Login to track your orders.
                                        </Typography>
                                        <Button fullWidth variant="outlined" size="medium"
                                            onClick={openLoginDialog}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                                            Login
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Register to track your orders and get faster checkout next time.
                                        </Typography>
                                        <Button fullWidth variant="outlined" size="medium"
                                            onClick={openRegDialog}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                                            Create Account
                                        </Button>
                                    </>
                                )
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Want faster checkout next time? Ask <strong>{biz.f_name}</strong> to add you as a registered customer.
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider', lineHeight: 1.6 }}>
                        Vendora88 is an order management platform only. We have no involvement in payments, goods, or fulfilment. All transactions are solely between you and <strong>{biz.f_name}</strong>.
                    </Typography>
                </Paper>
            </Box>

            {/* Inline registration dialog */}
            <Dialog open={regOpen} onClose={() => setRegOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    {regStep === 'send-otp' ? 'Create Your Account' : 'Verify & Set PIN'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
                    {regError && <Alert severity="error" onClose={() => setRegError('')}>{regError}</Alert>}

                    {regStep === 'send-otp' ? (
                        <>
                            <TextField
                                label="Name"
                                value={regName}
                                onChange={e => setRegName(e.target.value)}
                                size="small" fullWidth
                                inputProps={{ autoComplete: 'name' }}
                            />
                            <TextField
                                label="Mobile Number"
                                value={regMobile}
                                onChange={e => setRegMobile(e.target.value)}
                                size="small" fullWidth
                                inputProps={{ inputMode: 'tel' }}
                                error={!!regMobile.trim() && !!getMobileError(regMobile)}
                                helperText={(regMobile.trim() && getMobileError(regMobile)) || "We'll send a one-time password to this number"}
                            />
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                OTP sent to <strong>{regMobile}</strong>
                            </Typography>
                            <TextField
                                label="6-digit OTP"
                                value={regOtp}
                                onChange={e => setRegOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                size="small" fullWidth
                                inputProps={{ inputMode: 'numeric' }}
                                autoFocus
                            />
                            <TextField
                                label="Set a 6-digit PIN"
                                value={regPin}
                                onChange={e => setRegPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                size="small" fullWidth
                                type="password"
                                inputProps={{ inputMode: 'numeric' }}
                                helperText="You'll use this PIN to log in next time"
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    {regStep === 'verify-otp' && (
                        <Button variant="text" onClick={() => setRegStep('send-otp')} sx={{ textTransform: 'none', mr: 'auto' }}>
                            Back
                        </Button>
                    )}
                    <Button onClick={() => setRegOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={regStep === 'send-otp' ? handleSendOtp : handleVerifyAndRegister}
                        disabled={regLoading || (regStep === 'send-otp' ? !regMobile.trim() || !!getMobileError(regMobile) : regOtp.length !== 6 || regPin.length !== 6)}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {regLoading ? <CircularProgress size={20} color="inherit" /> : regStep === 'send-otp' ? 'Send OTP' : 'Create Account & Login'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Login dialog for existing users */}
            <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Login</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
                    {loginError && <Alert severity="error" onClose={() => setLoginError('')}>{loginError}</Alert>}
                    <TextField
                        label="Mobile Number"
                        value={form.phone}
                        size="small" fullWidth
                        InputProps={{ readOnly: true }}
                    />
                    <TextField
                        label="6-digit PIN"
                        value={loginPin}
                        onChange={e => setLoginPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        size="small" fullWidth
                        type="password"
                        inputProps={{ inputMode: 'numeric' }}
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setLoginOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained"
                        onClick={handleLogin}
                        disabled={loginLoading || loginPin.length !== 6}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {loginLoading ? <CircularProgress size={20} color="inherit" /> : 'Login'}
                    </Button>
                </DialogActions>
            </Dialog>
            </>
        );
    }

    // ── Plan: checkout ──────────────────────────────────────────────────────
    if (step === 'plan-checkout') {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', maxWidth: 720, mx: 'auto', boxShadow: { md: '0 0 30px rgba(0,0,0,0.08)' } }}>
                <Box sx={{ bgcolor: 'primary.main', px: 2, pb: 1.5, pt: 'calc(12px + env(safe-area-inset-top, 0px))', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <IconButton onClick={() => setStep('browse')} sx={{ color: 'white', p: 0.5 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" fontWeight={700} color="white">Your Subscription</Typography>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pb: 12 }}>
                    {planError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPlanError('')}>{planError}</Alert>}

                    <SectionLabel>Subscriptions</SectionLabel>
                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 2.5 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            {planItems.map(it => {
                                const p = planProducts.find(x => x.f_id === it.product_id);
                                const net = (parseFloat(p?.current_price) || 0) - (parseFloat(p?.current_discount) || 0);
                                return (
                                    <Box key={it.product_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                                        <Typography variant="body2">{p?.f_name || 'Plan'} × {it.quantity}</Typography>
                                        <Typography variant="body2" color="text.secondary">{fmt(net * it.quantity)}</Typography>
                                    </Box>
                                );
                            })}
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                                Billed as delivered — nothing is charged now.
                            </Typography>
                        </CardContent>
                    </Card>

                    <SectionLabel>Start from</SectionLabel>
                    <TextField type="date" fullWidth size="small" value={planStartDate}
                        onChange={e => setPlanStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} inputProps={{ min: planTomorrowISO() }}
                        helperText="Earliest is tomorrow — the vendor confirms it"
                        sx={{ mb: 2.5 }} />

                    <SectionLabel>Your Details</SectionLabel>
                    <TextField label="Your name" fullWidth size="small" value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        InputProps={{ startAdornment: <Person sx={{ fontSize: 18, mr: 1, color: 'text.disabled' }} /> }}
                        sx={{ mb: 1.5 }} />
                    <TextField label="Mobile number" fullWidth size="small" value={form.phone}
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                        inputProps={{ inputMode: 'numeric' }}
                        InputProps={{ startAdornment: <Phone sx={{ fontSize: 18, mr: 1, color: 'text.disabled' }} /> }}
                        sx={{ mb: 1.5 }} />
                    <TextField label="Delivery address" fullWidth size="small" multiline minRows={2} value={form.address}
                        onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                        sx={{ mb: 2.5 }} />

                    <SectionLabel>Note (optional)</SectionLabel>
                    <TextField fullWidth size="small" value={planNote} onChange={e => setPlanNote(e.target.value)}
                        placeholder="e.g. deliver in the morning" />
                </Box>

                <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 720, mx: 'auto',
                    p: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', borderRadius: '16px 16px 0 0' }}>
                    <Button fullWidth variant="contained" size="large" onClick={submitPlanSignup} disabled={planSubmitting}
                        sx={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }}>
                        {planSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Send request'}
                    </Button>
                </Paper>
            </Box>
        );
    }

    // ── Plan: confirmed ─────────────────────────────────────────────────────
    if (step === 'plan-confirmed') {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, maxWidth: 720, mx: 'auto' }}>
                <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" fontWeight={800} textAlign="center">Request sent!</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1, mb: 3 }}>
                    {biz.f_name} will confirm your subscription{planCount > 1 ? 's' : ''} and the start date.
                </Typography>
                <Button variant="contained" onClick={() => { setPlanCart({}); setPlanNote(''); setStoreMode('subscription'); setStep('browse'); }}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Back to store
                </Button>
            </Box>
        );
    }

    // ── Checkout ────────────────────────────────────────────────────────────
    if (step === 'checkout') {
        const showDeliveryCard = isKnownCustomer && !editingAddress;

        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', maxWidth: 720, mx: 'auto', boxShadow: { md: '0 0 30px rgba(0,0,0,0.08)' } }}>
                <Box sx={{ bgcolor: 'primary.main', px: 2, pb: 1.5, pt: 'calc(12px + env(safe-area-inset-top, 0px))', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <IconButton onClick={() => setStep('browse')} sx={{ color: 'white', p: 0.5 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" fontWeight={700} color="white">Your Order</Typography>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pb: 12 }}>

                    {/* Fulfillment toggle — only when mode supports pickup */}
                    {showFulfillmentToggle && (
                        <Box sx={{ mb: 2.5 }}>
                            <SectionLabel>How would you like it?</SectionLabel>
                            <ToggleButtonGroup
                                value={fulfillmentType}
                                exclusive
                                onChange={(_, val) => { if (val) setFulfillmentType(val); }}
                                fullWidth
                                size="small"
                            >
                                <ToggleButton value="delivery" sx={{ textTransform: 'none', fontWeight: 600, gap: 0.75 }}>
                                    <LocalShipping sx={{ fontSize: 18 }} /> Delivery
                                </ToggleButton>
                                <ToggleButton value="pickup" sx={{ textTransform: 'none', fontWeight: 600, gap: 0.75 }}>
                                    <StorePickupIcon sx={{ fontSize: 18 }} /> Store Pickup
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {!fulfillmentType && (
                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                    Please choose Delivery or Store Pickup.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Items summary */}
                    <SectionLabel>Items</SectionLabel>
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 2.5 }}>
                        {cartProducts.map(p => {
                            const net = (parseFloat(p.current_price) || 0) - (parseFloat(p.current_discount) || 0);
                            return (
                                <Box key={p.f_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                    <Typography variant="body2">{p.f_name} ×{cart[p.f_id]}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{fmt(net * cart[p.f_id])}</Typography>
                                </Box>
                            );
                        })}
                        {deliveryCharge > 0 && (
                            <>
                                <Divider sx={{ my: 0.75 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {biz.delivery_product_name || 'Delivery charge'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>{fmt(deliveryCharge)}</Typography>
                                </Box>
                            </>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
                            <Typography variant="subtitle2" fontWeight={700} color="primary.main">{fmt(cartTotal)}</Typography>
                        </Box>
                    </Paper>

                    {/* Delivery / Pickup details */}
                    <SectionLabel>{fulfillmentType === 'pickup' ? 'Pickup Details' : 'Delivery Details'}</SectionLabel>

                    {/* Pickup — collect from vendor; guests still need name + phone */}
                    {fulfillmentType === 'pickup' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
                            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    You'll collect your order directly from <strong>{biz.f_name}</strong>.
                                </Typography>
                            </Paper>
                            {!loggedInUser && (
                                <>
                                    <TextField size="small" fullWidth label="Your Name *"
                                        value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                        inputProps={{ autoComplete: 'name' }} />
                                    <TextField size="small" fullWidth label="Mobile Number *"
                                        value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                        inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                                        error={!!phoneError}
                                        helperText={phoneError} />
                                </>
                            )}
                            {/* Address stays visible on pickup too — optional */}
                            <TextField size="small" fullWidth label="Door / Flat Number (optional)"
                                value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                        </Box>
                    ) : showDeliveryCard ? (
                        <Paper elevation={0} sx={{ border: '1.5px solid', borderColor: 'success.light', borderRadius: 2, p: 1.75, mb: 1.5, bgcolor: 'rgba(46,125,50,0.04)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="caption" fontWeight={700} color="success.dark"
                                    sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
                                    Delivering to
                                </Typography>
                                <Button size="small" startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                                    onClick={() => setEditingAddress(true)}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', p: 0, minWidth: 0 }}>
                                    Change
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Person sx={{ fontSize: 15, color: 'text.secondary' }} />
                                    <Typography variant="body2" fontWeight={600}>{customerProfile.f_name}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Phone sx={{ fontSize: 15, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">{customerProfile.f_mobile}</Typography>
                                </Box>
                                {customerProfile.f_address_line1 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Home sx={{ fontSize: 15, color: 'text.secondary' }} />
                                        <Typography variant="body2">{customerProfile.f_address_line1}</Typography>
                                    </Box>
                                )}
                                {selectedAreaName && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <LocationOn sx={{ fontSize: 15, color: 'primary.main' }} />
                                        <Typography variant="body2" color="primary.main" fontWeight={600}>{selectedAreaName}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    ) : (
                        /* New customer or editing — show the form */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
                            {/* Name + phone: read-only for logged-in users */}
                            {loggedInUser ? (
                                <>
                                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 2, p: 1.5, bgcolor: 'rgba(37,99,235,0.04)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person sx={{ color: 'primary.main', fontSize: 16 }} />
                                            <Typography variant="body2" fontWeight={600}>{loggedInUser.f_name || 'Account holder'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
                                            <Phone sx={{ color: 'text.secondary', fontSize: 15 }} />
                                            <Typography variant="body2" color="text.secondary">{loggedInUser.f_mobile}</Typography>
                                        </Box>
                                    </Paper>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="text" onClick={handlePublicLogout}
                                            sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', p: 0, minWidth: 0 }}>
                                            Not you? Sign out
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <TextField size="small" fullWidth label="Your Name *"
                                        value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                        inputProps={{ autoComplete: 'name' }} />
                                    <TextField size="small" fullWidth label="Mobile Number *"
                                        value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                        inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                                        error={!!phoneError}
                                        helperText={phoneError} />
                                </>
                            )}
                            <TextField size="small" fullWidth label="Door / Flat Number (optional)"
                                value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                            {areas.length > 0 && (
                                <Box>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                                        Select your community *
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {areas.map(a => (
                                            <Chip key={a.f_id}
                                                label={a.f_name}
                                                clickable
                                                onClick={() => {
                                                    setFormAreaId(prev => prev === a.f_id ? null : a.f_id);
                                                    setGpsAreaDetected(false);
                                                }}
                                                color={formAreaId === a.f_id ? 'primary' : 'default'}
                                                variant={formAreaId === a.f_id ? 'filled' : 'outlined'}
                                                icon={formAreaId === a.f_id && gpsAreaDetected
                                                    ? <MyLocation sx={{ fontSize: '14px !important' }} />
                                                    : undefined}
                                                sx={{ height: 36, fontSize: '0.88rem', fontWeight: formAreaId === a.f_id ? 700 : 400 }}
                                            />
                                        ))}
                                    </Box>
                                    {gpsAreaDetected && formAreaId && (
                                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                                            Auto-detected from your location
                                        </Typography>
                                    )}
                                    {areas.length > 0 && !formAreaId && (
                                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                            Please select your community
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {/* Delivery preview once area chosen */}
                            {(selectedAreaName || form.address) && (
                                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'success.light', borderRadius: 2, p: 1.25, bgcolor: 'rgba(46,125,50,0.04)' }}>
                                    <Typography variant="caption" fontWeight={700} color="success.dark"
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                                        Delivering to
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            <Typography variant="body2" fontWeight={600}>
                                                {loggedInUser ? loggedInUser.f_name : form.name || '—'}
                                            </Typography>
                                        </Box>
                                        {form.address && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Home sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="body2">{form.address}</Typography>
                                            </Box>
                                        )}
                                        {selectedAreaName && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <LocationOn sx={{ fontSize: 14, color: 'primary.main' }} />
                                                <Typography variant="body2" color="primary.main" fontWeight={600}>{selectedAreaName}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            )}
                            {editingAddress && (
                                <Button variant="text" onClick={() => { setEditingAddress(false); setForm({ name: customerProfile.f_name || '', phone: customerProfile.f_mobile || '', address: customerProfile.f_address_line1 || '' }); setFormAreaId(customerProfile.f_area_id || null); }}
                                    sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start', p: 0 }}>
                                    ← Use saved address
                                </Button>
                            )}
                        </Box>
                    )}

                    <Box sx={{ mt: 2.5 }}>
                        <SectionLabel>Order Notes</SectionLabel>
                        <TextField
                            fullWidth size="small" multiline minRows={2}
                            placeholder="Any instructions for the vendor? (optional)"
                            value={orderNotes}
                            onChange={(e) => setOrderNotes(e.target.value)}
                        />
                    </Box>

                    {submitError && <Alert severity="error" sx={{ mt: 1 }}>{submitError}</Alert>}
                </Box>

                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, px: 2, pt: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))', bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button fullWidth variant="contained" size="large"
                        disabled={!canSubmit || submitting} onClick={handlePlaceOrder}
                        sx={{ height: 52, fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: 2 }}>
                        {submitting ? <CircularProgress size={22} color="inherit" /> : `Place Order · ${fmt(cartTotal)}`}
                    </Button>
                </Box>
            </Box>
        );
    }

    // ── Browse ──────────────────────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: (cartCount > 0 || planCount > 0) ? 10 : 3, maxWidth: 720, mx: 'auto', boxShadow: { md: '0 0 30px rgba(0,0,0,0.08)' } }}>
            {/* Business header */}
            <Box sx={{ bgcolor: 'primary.main', px: 2, pb: 2.5, pt: 'calc(20px + env(safe-area-inset-top, 0px))', color: 'white', textAlign: 'center', position: 'relative' }}>
                {onBack && (
                    <IconButton onClick={onBack} size="small"
                        sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'white' }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <BusinessLogo src={biz.f_logo_url} name={biz.f_name} size={48}
                        radius="10px" bgcolor="rgba(255,255,255,0.25)" color="#fff" />
                    <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: 0.5 }}>{biz.f_name}</Typography>
                </Box>
                {(biz.f_address1 || biz.f_city) && (
                    <Typography variant="body2" sx={{ opacity: 0.82 }}>
                        {[biz.f_address1, biz.f_city].filter(Boolean).join(', ')}
                    </Typography>
                )}
                {biz.f_phone && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.75 }}>
                        <Box component="a" href={`tel:${biz.f_phone}`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'white', textDecoration: 'none',
                                bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 1.5, px: 1.75, py: 0.6, fontWeight: 600, fontSize: '0.85rem' }}>
                            <Phone sx={{ fontSize: 16 }} /> Call
                        </Box>
                        <Box component="a" href={`https://wa.me/${waNumber(biz.f_phone)}`} target="_blank" rel="noopener"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'white', textDecoration: 'none',
                                bgcolor: '#25D366', borderRadius: 1.5, px: 1.75, py: 0.6, fontWeight: 700, fontSize: '0.85rem' }}>
                            <WhatsAppIcon sx={{ fontSize: 16 }} /> WhatsApp
                        </Box>
                    </Box>
                )}
                {loggedInUser && (
                    <Box
                        onClick={() => { window.location.href = '/?view=customer'; }}
                        sx={{
                            position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 10px)', right: 12,
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: 'rgba(255,255,255,0.22)', borderRadius: 2,
                            px: 1.25, py: 0.4, cursor: 'pointer',
                        }}
                    >
                        <Home sx={{ fontSize: 13 }} />
                        <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 120 }}>
                            {loggedInUser.f_name || loggedInUser.f_mobile}
                        </Typography>
                    </Box>
                )}
                {!loggedInUser && (
                    <Button size="small" variant="contained"
                        onClick={() => setShowAuth(true)}
                        sx={{
                            position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 10px)', right: 12,
                            bgcolor: 'white', color: 'primary.main', fontSize: '0.72rem', py: 0.25, fontWeight: 700,
                            '&:hover': { bgcolor: 'grey.100' },
                        }}>
                        Login / Sign up
                    </Button>
                )}
            </Box>

            {/* Store note — the vendor's own note, else a default note (order mode) */}
            {(() => {
                const note = biz.f_online_store_notes || (storeMode === 'order' ? DEFAULT_ORDER_NOTE : '');
                if (!note) return null;
                return (
                    <Box sx={{ bgcolor: '#fff8e1', borderBottom: '1px solid', borderColor: '#ffe082', px: 2, py: 1.25 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#f57c00', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.25 }}>
                            Please note
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5d4037', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {note}
                        </Typography>
                    </Box>
                );
            })()}

            {/* Communities we serve — informational */}
            {areas.length > 0 && (
                <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: 2, pt: 1.25, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="caption" fontWeight={700} color="primary.main"
                            sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            We deliver to these communities
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {areas.map(a => (
                            <Chip key={a.f_id} label={a.f_name} size="small"
                                icon={<LocationOn sx={{ fontSize: '14px !important' }} />}
                                sx={{ height: 28, fontSize: '0.8rem', bgcolor: 'rgba(37,99,235,0.08)', color: 'primary.dark', fontWeight: 500, '& .MuiChip-icon': { color: 'primary.main' } }}
                            />
                        ))}
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                        You'll select your community at checkout
                    </Typography>
                </Box>
            )}

            {/* Order / Subscription toggle — only when business has plan products */}
            {planProducts.length > 0 && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                    <Box sx={{ display: 'flex', bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 2.5, p: 0.4, gap: 0.4 }}>
                        {[
                            { val: 'order', icon: <CartIcon sx={{ fontSize: 17 }} />, label: 'One-time Order' },
                            { val: 'subscription', icon: <SubscriptionIcon sx={{ fontSize: 17 }} />, label: 'Subscription Plan' },
                        ].map(({ val, icon, label }) => (
                            <Button key={val} fullWidth
                                onClick={() => setStoreMode(val)}
                                startIcon={icon}
                                sx={{
                                    textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8,
                                    fontSize: '0.85rem',
                                    ...(storeMode === val
                                        ? { bgcolor: 'background.paper', boxShadow: '0 1px 4px rgba(0,0,0,0.14)', color: 'primary.main', '&:hover': { bgcolor: 'background.paper' } }
                                        : { color: 'text.secondary', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }),
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Sticky category navigation — switches based on mode */}
            {storeMode === 'order' ? (
                <StickyCategoryBar
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onSelect={scrollToCategory}
                    stickyBarRef={stickyBarRef}
                    setChipRef={setChipRef}
                    top={0}
                    sx={{ px: 1.5, pt: `calc(8px + ${SAFE_TOP})` }}
                />
            ) : (
                planCategories.length > 1 && (
                    <StickyCategoryBar
                        categories={planCategories}
                        selectedCategory={planSpy.selectedCategory}
                        onSelect={planSpy.scrollToCategory}
                        stickyBarRef={planSpy.stickyBarRef}
                        setChipRef={planSpy.setChipRef}
                        top={0}
                        sx={{ px: 1.5, pt: `calc(8px + ${SAFE_TOP})` }}
                    />
                )
            )}

            {/* Products grouped by category — order mode */}
            {storeMode === 'order' && (
                (catalog.products || []).length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="body2" color="text.secondary">No products available right now</Typography>
                    </Box>
                ) : (
                    categories.map((cat) => (
                        <Box
                            key={cat}
                            ref={setSectionRef(cat)}
                            data-cat={cat}
                            sx={{ px: 1.5, pt: 1.25, scrollMarginTop: sectionScrollMargin(SAFE_TOP) }}
                        >
                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {cat}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.75 }}>
                                {grouped[cat].map(product => {
                                    const price    = parseFloat(product.current_price) || 0;
                                    const disc     = parseFloat(product.current_discount) || 0;
                                    const netPrice = price - disc;
                                    const qty      = cart[product.f_id] || 0;
                                    const minQty   = parseFloat(product.f_min_order_qty) || 1;
                                    const stepQty  = parseFloat(product.f_increment_order_qty) || 1;
                                    return (
                                        <Card key={product.f_id} variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                                            <CardContent sx={{ p: '12px 16px !important' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                    <Box onClick={() => setDetailProduct(product)}
                                                        sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}>
                                                        <ProductImage src={product.f_image_url} name={product.f_name} fontSize="1.25rem" />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                                            {product.f_name}
                                                        </Typography>
                                                        {product.f_description && (
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                                                                {product.f_description}
                                                            </Typography>
                                                        )}
                                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
                                                            <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                                                {fmt(netPrice)}
                                                            </Typography>
                                                            {product.f_unit && (
                                                                <Typography variant="caption" color="text.secondary">/ {product.f_unit}</Typography>
                                                            )}
                                                            {disc > 0 && (
                                                                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                                                                    {fmt(price)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {minQty > 1 && (
                                                            <Typography variant="caption" color="text.disabled">
                                                                Min: {minQty}{stepQty !== 1 ? ` · Step: ${stepQty}` : ''}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flexShrink: 0 }}>
                                                        {qty === 0 ? (
                                                            <Button variant="outlined" size="small"
                                                                onClick={() => addProduct(product)}
                                                                sx={{ minWidth: 72, height: 36, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                                                                Add
                                                            </Button>
                                                        ) : (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', border: '1.5px solid', borderColor: 'primary.main', borderRadius: 2, overflow: 'hidden' }}>
                                                                <IconButton size="small" onClick={() => removeProduct(product)}
                                                                    sx={{ color: 'primary.main', borderRadius: 0, p: 0.75 }}>
                                                                    <Remove sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                                <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontSize: '0.95rem' }}>
                                                                    {qty}
                                                                </Typography>
                                                                <IconButton size="small" onClick={() => addProduct(product)}
                                                                    sx={{ color: 'primary.main', borderRadius: 0, p: 0.75 }}>
                                                                    <Add sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))
                )
            )}

            {/* Products grouped by category — subscription mode */}
            {storeMode === 'subscription' && (
                planProducts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="body2" color="text.secondary">No subscriptions available right now</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ px: 2, pt: 1.25, pb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                                Pick what you want delivered regularly. {biz.f_name} will confirm before it starts.
                            </Typography>
                        </Box>
                        {planCategories.map(cat => (
                            <Box key={cat} ref={planSpy.setSectionRef(cat)} data-cat={cat}
                                sx={{ px: 1.5, pt: 1.25, scrollMarginTop: sectionScrollMargin(SAFE_TOP) }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary"
                                    sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    {cat}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.75 }}>
                                    {planGrouped[cat].map(product => {
                                        const price    = parseFloat(product.current_price) || 0;
                                        const disc     = parseFloat(product.current_discount) || 0;
                                        const netPrice = price - disc;
                                        const qty      = planCart[product.f_id] || 0;
                                        return (
                                            <Card key={product.f_id} variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                                                <CardContent sx={{ p: '12px 16px !important' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                        <Box sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                                            <ProductImage src={product.f_image_url} name={product.f_name} fontSize="1.25rem" />
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                                                {product.f_name}
                                                            </Typography>
                                                            {product.f_description && (
                                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                                                                    {product.f_description}
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
                                                                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                                                    {fmt(netPrice)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {product.f_billing_type === 'MonthlyFlatFee'
                                                                        ? '/ month'
                                                                        : product.f_unit ? `/ ${product.f_unit}` : '/ delivery'}
                                                                </Typography>
                                                                {disc > 0 && (
                                                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                                                                        {fmt(price)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ flexShrink: 0 }}>
                                                            {qty === 0 ? (
                                                                <Button variant="outlined" size="small"
                                                                    onClick={() => setPlanQty(product.f_id, 1)}
                                                                    sx={{ minWidth: 72, height: 36, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                                                                    Add
                                                                </Button>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', border: '1.5px solid', borderColor: 'primary.main', borderRadius: 2, overflow: 'hidden' }}>
                                                                    <IconButton size="small" onClick={() => setPlanQty(product.f_id, -1)}
                                                                        sx={{ color: 'primary.main', borderRadius: 0, p: 0.75 }}>
                                                                        <Remove sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                    <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontSize: '0.95rem' }}>
                                                                        {qty}
                                                                    </Typography>
                                                                    <IconButton size="small" onClick={() => setPlanQty(product.f_id, 1)}
                                                                        sx={{ color: 'primary.main', borderRadius: 0, p: 0.75 }}>
                                                                        <Add sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </>
                )
            )}

            {/* Floating cart bar — order mode */}
            {storeMode === 'order' && cartCount > 0 && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, px: 1.5, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', boxShadow: '0 -4px 16px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        onClick={() => setCartReviewOpen(true)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                        <CartIcon color="primary" />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                                {cartCount} item{cartCount > 1 ? 's' : ''} · {fmt(cartSubtotal)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                                <Typography variant="caption" fontWeight={600}>View cart</Typography>
                                <ExpandLess sx={{ fontSize: 16 }} />
                            </Box>
                        </Box>
                    </Box>
                    <Button variant="contained" size="large"
                        onClick={() => { setEditingAddress(false); setStep('checkout'); }}
                        sx={{ height: 48, fontWeight: 700, textTransform: 'none', borderRadius: 2, flexShrink: 0, px: 2.5 }}>
                        Checkout →
                    </Button>
                </Box>
            )}

            {/* Floating cart bar — subscription mode */}
            {storeMode === 'subscription' && planCount > 0 && (
                <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 720, mx: 'auto',
                    px: 1.5, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', borderRadius: '16px 16px 0 0', zIndex: 1200 }}>
                    <Button fullWidth variant="contained" size="large"
                        onClick={() => { setPlanError(''); setStep('plan-checkout'); }}
                        sx={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }}>
                        Continue · {planCount} subscription{planCount > 1 ? 's' : ''}
                    </Button>
                </Paper>
            )}

            {/* Cart review sheet */}
            <CartReviewSheet
                open={cartReviewOpen}
                onClose={() => setCartReviewOpen(false)}
                items={cartProducts.map(p => {
                    const net = Math.max(0, (parseFloat(p.current_price) || 0) - (parseFloat(p.current_discount) || 0));
                    return { id: p.f_id, name: p.f_name, imageUrl: p.f_image_url, unitPrice: net, quantity: cart[p.f_id], raw: p };
                })}
                onInc={(it) => addProduct(it.raw)}
                onDec={(it) => removeProduct(it.raw)}
                total={cartSubtotal}
                primaryLabel="Proceed to Checkout"
                onPrimary={() => { setCartReviewOpen(false); setEditingAddress(false); setStep('checkout'); }}
            />

            {/* Product detail sheet (tap image to enlarge) */}
            <ProductDetailSheet
                product={detailProduct && {
                    name: detailProduct.f_name,
                    imageUrl: detailProduct.f_image_url,
                    unitPrice: Math.max(0, (parseFloat(detailProduct.current_price) || 0) - (parseFloat(detailProduct.current_discount) || 0)),
                    originalPrice: parseFloat(detailProduct.current_price) || 0,
                    description: detailProduct.f_description || null,
                }}
                quantity={detailProduct ? (cart[detailProduct.f_id] || 0) : 0}
                onAdd={() => addProduct(detailProduct)}
                onInc={() => addProduct(detailProduct)}
                onDec={() => removeProduct(detailProduct)}
                onClose={() => setDetailProduct(null)}
            />
        </Box>
    );
}
